import isEqual from 'underscore/modules/isEqual';
import { PageStrings } from './page_strings_util';
import { StringConstantsMixin } from '../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { getQueryStringValue } from './window_helpers';

const ET_PHOTO_USER_ID = '13073460';
const ET_PHOTO_USER_SLUG = 'alltrails-user';

const SearchFiltersUtil = {
  valueToStringKey(value) {
    return value.toUpperCase().replace(/-/g, '_');
  },
  getSearchFilters(context, page, initialUserSlug = null) {
    let filters = {};
    filters.initialUserSlug = initialUserSlug;
    filters.mobileMap = false;
    filters.queryTerm = null;
    filters.completed = {};
    ['uncompleted', 'unverified', 'verified'].forEach(value => {
      filters.completed[value] = {
        value,
        name: StringConstantsMixin.strings.FILTERS[this.valueToStringKey(value)],
        selected: false
      };
    });
    filters.linked = {};
    ['unlinked', 'linked'].forEach(value => {
      filters.linked[value] = {
        value,
        name: StringConstantsMixin.strings.FILTERS[`TRAIL_${this.valueToStringKey(value)}`],
        selected: false
      };
    });
    filters.activities = {};
    [
      'backpacking',
      'bike-touring',
      'birding',
      'camping',
      'cross-country-skiing',
      'fishing',
      'hiking',
      'horseback-riding',
      'mountain-biking',
      'nature-trips',
      'off-road-driving',
      'paddle-sports',
      'road-biking',
      'rock-climbing',
      'scenic-driving',
      'skiing',
      'snowshoeing',
      'trail-running',
      'via-ferrata',
      'walking'
    ].forEach(value => {
      filters.activities[value] = {
        value,
        name: StringConstantsMixin.strings.FILTERS.ACTIVITIES[this.valueToStringKey(value)],
        selected: false
      };
    });
    filters.features = {};
    [
      'beach',
      'cave',
      'city-walk',
      'event',
      'forest',
      'historic-site',
      'hot-springs',
      'lake',
      'pub-crawl',
      'rails-trails',
      'river',
      'views',
      'waterfall',
      'wildflowers',
      'wildlife'
    ].forEach(value => {
      filters.features[value] = {
        value,
        name: StringConstantsMixin.strings.FILTERS.FEATURE_TYPES[this.valueToStringKey(value)],
        selected: false
      };
    });
    filters.access = {};
    ['dogs', 'paved', 'kids', 'partially-paved', 'ada', 'strollers'].forEach(value => {
      filters.access[value] = {
        value: value === 'dogs' ? ['dogs', 'dogs-leash'] : value,
        name: StringConstantsMixin.strings.FILTERS.ACCESS[this.valueToStringKey(value)],
        selected: false
      };
    });
    // will be populated by Algolia search results
    filters.countries = {};
    filters.states = {};
    filters.cities = {};
    filters.areas = {};
    filters.difficulty = {
      easy: {
        value: [1, 2],
        name: StringConstantsMixin.strings.DIFFICULTY.NORMAL.EASY,
        selected: false
      },
      moderate: {
        value: [3, 4],
        name: StringConstantsMixin.strings.DIFFICULTY.NORMAL.MODERATE,
        selected: false
      },
      hard: {
        value: [5, 6, 7, 8],
        name: StringConstantsMixin.strings.DIFFICULTY.NORMAL.HARD,
        selected: false
      }
    };
    filters.route_type = {
      L: {
        value: 'L',
        name: StringConstantsMixin.strings.FILTERS.ROUTE_TYPES.LOOP,
        selected: false
      },
      O: {
        value: 'O',
        name: StringConstantsMixin.strings.FILTERS.ROUTE_TYPES.OUT_AND_BACK,
        selected: false
      },
      P: {
        value: 'P',
        name: StringConstantsMixin.strings.FILTERS.ROUTE_TYPES.POINT_TO_POINT,
        selected: false
      }
    };
    filters.visitor_usage = {
      '1': {
        value: '1',
        name: StringConstantsMixin.strings.FILTERS.USAGES.LIGHT,
        selected: false
      },
      '2': {
        value: '2',
        name: StringConstantsMixin.strings.FILTERS.USAGES.MODERATE,
        selected: false
      },
      '3': {
        value: ['3', '4'],
        name: StringConstantsMixin.strings.FILTERS.USAGES.HEAVY,
        selected: false
      }
    };

    if (context.displayMetric) {
      filters.lengths = {
        // these are indices rather than actual values, their values are determined by the function below
        initialRange: [0, 70],
        // 1 - 50 step by 1, 50 - 150 step by 5
        stepIncreaseAt: 50,
        stepIncreaseBy: 5,
        upperBound: 70,
        displayFunction: 'km',
        multiplier: 1000.0,
        value: [0, -1],
        displayValue: [0, 70],
        formattedRange: ['0', '150+']
      };
    } else {
      filters.lengths = {
        // these are indices rather than actual values, their values are determined by the function below
        initialRange: [0, 44],
        // 1 - 30 step by 1, 30 - 100 step by 5
        stepIncreaseAt: 30,
        stepIncreaseBy: 5,
        upperBound: 44,
        displayFunction: 'mi',
        multiplier: 1609.344,
        value: [0, -1],
        displayValue: [0, 44],
        formattedRange: ['0', '100+']
      };
    }

    if (context.displayMetric) {
      filters.elevation_gain = {
        initialRange: [0, 3000],
        // normal for now
        stepIncreaseAt: null,
        stepIncreaseBy: null,
        upperBound: 3000,
        displayFunction: 'm',
        multiplier: 1,
        value: [0, -1],
        displayValue: [0, 3000],
        formattedRange: ['0', '3000+']
      };
    } else {
      filters.elevation_gain = {
        initialRange: [0, 10000],
        // normal for now
        stepIncreaseAt: null,
        stepIncreaseBy: null,
        upperBound: 10000,
        displayFunction: 'ft',
        multiplier: 0.3048,
        value: [0, -1],
        displayValue: [0, 10000],
        formattedRange: ['0', '10,000+']
      };
    }

    filters.time_duration = {
      initialRange: [0, 48],
      // normal for now
      stepIncreaseAt: 0,
      stepIncreaseBy: 15,
      upperBound: 48,
      displayFunction: 'duration',
      multiplier: 1,
      value: [0, -1],
      displayValue: [0, 48],
      formattedRange: ['0', 'multi-day']
    };

    filters = this.initSortFilterOptions(filters, page);
    filters.trailIds = [];
    filters.trackIds = [];
    filters.mapIds = [];
    filters.minRating = null;
    filters.boundingBox = null;
    filters.zoomLevel = null;
    filters.locationObject = null;
    filters.initLocationObject = null;
    return filters;
  },
  formatTimeDuration(valueMinutes) {
    if (valueMinutes === 'multi-day' || valueMinutes === '720+') {
      return StringConstantsMixin.strings.TIME.MULTI_DAY;
    }
    const mins = parseInt(valueMinutes);
    const hrs = Math.floor(mins / 60);
    let string = '';
    if (hrs >= 1) {
      const remainderMins = mins % 60;
      string = `${hrs} h ${remainderMins} m`;
    } else {
      string = `${mins} m`;
    }
    return string;
  },
  getFilterDisplayFunction(key) {
    switch (key) {
      case 'km':
        return value => `${value} km`;
      case 'mi':
        return value => `${value} mi`;
      case 'm':
        return value => `${value} m`;
      case 'ft':
        return value => `${value} ft`;
      case 'duration':
        return this.formatTimeDuration;
      default:
        return value => value;
    }
  },
  initSortFilterOptions(filters, page) {
    filters.sort = {};
    if ([PageStrings.EXPLORE_ALL_PAGE, PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE].includes(page)) {
      filters.sort = {
        'best-match': {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.BEST_MATCH,
          direction: 0,
          selected: true
        },
        'most-popular': {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.MOST_POPULAR,
          direction: 0,
          selected: false
        },
        closest: {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.CLOSEST,
          direction: 0,
          selected: false
        }
      };
    } else if ([PageStrings.EXPLORE_CUSTOM_PAGE, PageStrings.EXPLORE_COMPLETED_PAGE, PageStrings.EXPLORE_FAVORITE_PAGE].includes(page)) {
      filters.sort = {
        'list-order': {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.LIST_ORDER,
          direction: 1,
          selected: true
        },
        date: {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.DATE,
          direction: 1,
          selected: false
        },
        name: {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.NAME,
          direction: 1,
          selected: false
        }
      };
    } else if ([PageStrings.SEO_PAGE].includes(page)) {
      filters.sort = {
        'most-popular': {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.MOST_POPULAR,
          direction: 0,
          selected: true
        }
      };
    } else {
      filters.sort = {
        date: {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.DATE,
          direction: 1,
          selected: true
        },
        name: {
          name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.NAME,
          direction: 1,
          selected: false
        }
      };
    }
    return filters;
  },
  toggleFilter(filters, filterType, filterToToggle) {
    if (filters.hasOwnProperty(filterType) && filters[filterType] != null && filters[filterType].hasOwnProperty(filterToToggle)) {
      filters[filterType][filterToToggle].selected = !filters[filterType][filterToToggle].selected;
    }
    return filters;
  },
  sliderFilter(filters, filterType, sliderValues) {
    if (filters[filterType] && filters[filterType].value !== sliderValues.convertedRange) {
      filters[filterType].value = sliderValues.convertedRange;
      filters[filterType].displayValue = sliderValues.displayRange;
      if (sliderValues.formattedRange) {
        filters[filterType].formattedRange = sliderValues.formattedRange;
      }
    }
    return filters;
  },
  toggleSortFilter(filters, sortFilterName) {
    if (filters.hasOwnProperty('sort') && filters.sort[sortFilterName] != null) {
      if (filters.sort[sortFilterName].selected == true) {
        filters.sort[sortFilterName].direction = filters.sort[sortFilterName].direction * -1;
      } else filters.sort[sortFilterName].selected = true;

      Object.keys(filters.sort).forEach(sortKey => {
        if (sortKey == sortFilterName) return;
        filters.sort[sortKey].selected = false;
      });
    }
    return filters;
  },
  setFilter(filters, filterToSet, value) {
    // Shortcut the queryTerm case, we always want to set it
    if (filters.hasOwnProperty(filterToSet) && (filters[filterToSet] != value || filterToSet == 'queryTerm')) {
      filters[filterToSet] = value;
    } else {
      filters[filterToSet] = null;
    }
    return filters;
  },
  setZoomLevelFilter(filters, value) {
    filters.zoomLevel = value;
    return filters;
  },
  setBoundsFilter(filters, latTopLeft, lngTopLeft, latBottomRight, lngBottomRight) {
    const mod = (n, m) => {
      return ((n % m) + m) % m;
    };
    const wrapBoundsLongitude = lng => {
      if (lng < 0 && lng > -180) return lng;
      if (lng > 0 && lng < 180) return lng;
      return mod(lng + 180, 360) - 180;
    };

    filters.boundingBox = {
      latitudeTopLeft: latTopLeft,
      longitudeTopLeft: wrapBoundsLongitude(lngTopLeft),
      latitudeBottomRight: latBottomRight,
      longitudeBottomRight: wrapBoundsLongitude(lngBottomRight)
    };
    return filters;
  },
  formatTrailIdArrayForAlgolia(ids, positive = true) {
    return `(${ids
      .map(id => {
        return `${positive ? '' : 'NOT '}objectID:trail-${id}`;
      })
      .join(positive ? ' OR ' : ' AND ')})`;
  },
  getAlgoliaCompletedFilterString(filters, page, completedIds, verifiedIds) {
    let completedFilterString = '';

    // find which keys are selected and unselected
    const selected = [];
    const unselected = [];
    Object.keys(filters.completed).forEach(itemKey => {
      const filterItem = filters.completed[itemKey];
      if (filterItem.selected) {
        selected.push(filterItem.value);
      } else {
        unselected.push(filterItem.value);
      }
    });

    // Form arrays of trail ids for completed, verified, and completed-unverified
    completedIds.push('NULL'); // allows correct filtering with empty list
    verifiedIds.push('NULL'); // allows correct filtering with empty list
    const unverifiedIds = completedIds.filter(id => {
      return verifiedIds.indexOf(id) < 0;
    });
    unverifiedIds.push('NULL');

    // Create filter string based on which of 3 options is either selected or unselected
    // (filter is not applied when all options are selected or all options are unselected)
    if (selected.length == 1) {
      // unselected.length == 2
      switch (selected[0]) {
        case 'uncompleted':
          completedFilterString = this.formatTrailIdArrayForAlgolia(unverifiedIds.concat(verifiedIds), false);
          break;
        case 'unverified':
          completedFilterString = this.formatTrailIdArrayForAlgolia(unverifiedIds);
          break;
        case 'verified':
          completedFilterString = this.formatTrailIdArrayForAlgolia(verifiedIds);
          break;
        default:
          break;
      }
    } else if (unselected.length == 1) {
      // selected.length == 2
      switch (unselected[0]) {
        case 'uncompleted':
          completedFilterString = this.formatTrailIdArrayForAlgolia(unverifiedIds.concat(verifiedIds));
          break;
        case 'unverified':
          completedFilterString = this.formatTrailIdArrayForAlgolia(unverifiedIds, false);
          break;
        case 'verified':
          completedFilterString = this.formatTrailIdArrayForAlgolia(verifiedIds, false);
          break;
        default:
          break;
      }
    }

    return completedFilterString;
  },
  createAlgoliaMultFilterString(filter, algoliaKeyAndComparator, joinOperator) {
    const filterSubStrings = [];
    Object.keys(filter).forEach(itemKey => {
      // ignore keys that aren't selected
      if (!filter[itemKey].selected) {
        return;
      }
      // if key is selected, add to joined filter strings, format differently if value is an object
      if (typeof filter[itemKey].value === 'object') {
        filterSubStrings.push(
          `(${filter[itemKey].value
            .map(val => {
              return algoliaKeyAndComparator + val;
            })
            .join(' OR ')})`
        );
      } else {
        filterSubStrings.push(algoliaKeyAndComparator + filter[itemKey].value);
      }
    });
    return filterSubStrings.join(` ${joinOperator} `);
  },
  createTypeIDFilterString(filters, page) {
    // Create trail id filter
    let trailIds;
    if (filters.trailIds && filters.trailIds.length > 0) {
      trailIds = `(${filters.trailIds.map(id => `objectID:trail-${id}`).join(' OR ')})`;
    }

    // Create track id filter
    let trackIds;
    if (filters.trackIds && filters.trackIds.length > 0) {
      trackIds = `(${filters.trackIds.map(id => `objectID:track-${id}`).join(' OR ')})`;
    }

    // Create map id filter
    let mapIds;
    if (filters.mapIds && filters.mapIds.length > 0) {
      mapIds = `(${filters.mapIds.map(id => `objectID:map-${id}`).join(' OR ')})`;
    }

    // Change search to type:tracks if on tracks page
    if (page === PageStrings.EXPLORE_USERS_TRACKS_PAGE && filters.initialUserSlug == ET_PHOTO_USER_SLUG) {
      return `type:track AND user.id:${ET_PHOTO_USER_ID}`;
    }
    if (page === PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE) {
      // Show AtMaps that are not tied to trails, not hidden, and that are not missing a polyline
      return '(type:map OR type:track) AND trail_id=0 AND hidden=0 AND missing_polyline=0';
    }
    const listPages = [PageStrings.EXPLORE_COMPLETED_PAGE, PageStrings.EXPLORE_FAVORITE_PAGE, PageStrings.EXPLORE_CUSTOM_PAGE];
    if (listPages.includes(page)) {
      if (!trailIds && !trackIds && !mapIds) {
        return `objectID:trail-null`;
      }
      return `${[trailIds, trackIds, mapIds].filter(ids => !!ids && ids.length > 0).join(' OR ')}`;
    }
    if (trailIds) {
      return trailIds;
    }
    return 'type:trail';
  },
  createAlgoliaQueryObject({ filters, mobileWidth, page, completedIds, verifiedIds }) {
    const algoliaQueryObject = { hitsPerPage: 1000 };

    const filterStrings = [];

    // Note for below: Any filters that are joined with OR's need to have grouping
    // parens put around them
    // Any filters that are joined with AND's cannot have grouping parens around them
    // as algolia will error

    // Create filters based on not-completed, completed, verified
    const completedFilterString = this.getAlgoliaCompletedFilterString(filters, page, completedIds, verifiedIds);
    if (completedFilterString.length > 0) {
      filterStrings.push(`(${completedFilterString})`);
    }

    // Create difficulty rating filters
    const difficultyRatingFilterString = this.createAlgoliaMultFilterString(filters.difficulty, 'difficulty_rating:', 'OR');
    if (difficultyRatingFilterString.length > 0) {
      filterStrings.push(`(${difficultyRatingFilterString})`);
    }

    // Create route type filters
    const routeTypeFilterString = this.createAlgoliaMultFilterString(filters.route_type, 'route_type:', 'OR');
    if (routeTypeFilterString.length > 0) {
      filterStrings.push(`(${routeTypeFilterString})`);
    }

    // Create trail traffic filters
    const trailTrafficFilterString = this.createAlgoliaMultFilterString(filters.visitor_usage, 'visitor_usage:', 'OR');
    if (trailTrafficFilterString.length > 0) {
      filterStrings.push(`(${trailTrafficFilterString})`);
    }

    // Create activity filters
    filterStrings.push(this.createAlgoliaMultFilterString(filters.activities, 'activities:', 'AND'));

    // Create feature filters
    filterStrings.push(this.createAlgoliaMultFilterString(filters.features, 'features:', 'AND'));

    // Create access filters (NOTE: access is under features in algolia, so we use features prefix)
    filterStrings.push(this.createAlgoliaMultFilterString(filters.access, 'features:', 'AND'));

    // Create country filters
    const countryFilterString = this.createAlgoliaMultFilterString(filters.countries, 'country_id=', 'OR');
    if (countryFilterString.length > 0) {
      filterStrings.push(`(${countryFilterString})`);
    }

    // Create state filters
    const stateFilterString = this.createAlgoliaMultFilterString(filters.states, 'state_id=', 'OR');
    if (stateFilterString.length > 0) {
      filterStrings.push(`(${stateFilterString})`);
    }

    // Create city filters
    const cityFilterString = this.createAlgoliaMultFilterString(filters.cities, 'city_id=', 'OR');
    if (cityFilterString.length > 0) {
      filterStrings.push(`(${cityFilterString})`);
    }

    // Create area filters
    const areaFilterString = this.createAlgoliaMultFilterString(filters.areas, 'area_id=', 'OR');
    if (areaFilterString.length > 0) {
      filterStrings.push(`(${areaFilterString})`);
    }

    // Create length filters
    let lengthFilterString = '';
    if (filters.lengths) {
      if (filters.lengths.value[1] === -1) {
        lengthFilterString = `${'(length>='}${filters.lengths.value[0]})`;
      } else {
        lengthFilterString = `${'(length:'}${filters.lengths.value[0]} TO ${filters.lengths.value[1]})`;
      }
    }
    if (lengthFilterString.length > 0) {
      filterStrings.push(`(${lengthFilterString})`);
    }

    // Create elevation filters
    let elevationFilterString = '';
    if (filters.elevation_gain) {
      if (filters.elevation_gain.value[1] === -1) {
        elevationFilterString = `${'(elevation_gain>='}${filters.elevation_gain.value[0]})`;
      } else {
        elevationFilterString = `${'(elevation_gain:'}${filters.elevation_gain.value[0]} TO ${filters.elevation_gain.value[1]})`;
      }
    }
    if (elevationFilterString.length > 0) {
      filterStrings.push(`(${elevationFilterString})`);
    }

    // Create time filters
    // only use in query if it has been adjusted, some trails do not have this info
    let timeFilterString = '';
    if (filters.time_duration && !isEqual(filters.time_duration.value, [0, -1])) {
      if (filters.time_duration.value[1] === -1) {
        timeFilterString = `${'(duration_minutes>='}${filters.time_duration.value[0]})`;
      } else {
        timeFilterString = `${'(duration_minutes:'}${filters.time_duration.value[0]} TO ${filters.time_duration.value[1]})`;
      }
    }
    if (timeFilterString.length > 0) {
      filterStrings.push(`(${timeFilterString})`);
    }

    // Create minRating filter
    if (filters.minRating) {
      filterStrings.push(`avg_rating >= ${filters.minRating}`);
    }

    // Create string that filters on type and id (used for lists - different than completed/verified)
    const typeIDFilterString = this.createTypeIDFilterString(filters, page);
    if (typeIDFilterString && typeIDFilterString.length > 0) {
      filterStrings.push(typeIDFilterString);
    }

    // Join the filterStrings with AND
    algoliaQueryObject.filters = filterStrings.filter(f => f.length > 0).join(' AND ');

    // Set insideBoundingBox field on algoliaQueryObject
    const boundingBox = this.getBoundingBox(filters, mobileWidth, page);
    if (boundingBox) {
      algoliaQueryObject.insideBoundingBox = boundingBox;
    }

    return algoliaQueryObject;
  },
  createAlgoliaLocQueryObject(types, filters, mobileWidth, page) {
    const algoliaQueryObject = {
      hitsPerPage: 200,
      filters: types.map(type => `type:${type}`).join(' OR ')
    };

    // Set insideBoundingBox field on algoliaQueryObject
    const boundingBox = this.getBoundingBox(filters, mobileWidth, page);
    if (boundingBox) {
      algoliaQueryObject.insideBoundingBox = boundingBox;
    }

    return algoliaQueryObject;
  },
  getBoundingBox(filters, mobileWidth, page) {
    // if were zoomed out far enough on certain pages, we just want all the results from the other filters, ignore bounding box
    if (page === PageStrings.EXPLORE_FAVORITE_PAGE || page === PageStrings.EXPLORE_CUSTOM_PAGE || page === PageStrings.EXPLORE_COMPLETED_PAGE) {
      if (filters.zoomLevel != null && filters.zoomLevel < 4) {
        return null;
      }
    }
    // otherwise, find bounding box
    if (filters.boundingBox != null && (filters.zoomLevel == null || (filters.zoomLevel && filters.zoomLevel >= 3))) {
      let boundingBox;
      if (mobileWidth) {
        const degreeOffset = 0.703;
        boundingBox = [
          filters.boundingBox.latitudeTopLeft + degreeOffset,
          filters.boundingBox.longitudeTopLeft - degreeOffset,
          filters.boundingBox.latitudeBottomRight - degreeOffset,
          filters.boundingBox.longitudeBottomRight + degreeOffset
        ].join(',');
      } else if (filters.boundingBox.longitudeTopLeft > filters.boundingBox.longitudeBottomRight) {
        boundingBox = [
          filters.boundingBox.latitudeTopLeft,
          filters.boundingBox.longitudeTopLeft,
          filters.boundingBox.latitudeBottomRight,
          180,
          filters.boundingBox.latitudeTopLeft,
          -179.999,
          filters.boundingBox.latitudeBottomRight,
          filters.boundingBox.longitudeBottomRight
        ].join(',');
      } else {
        boundingBox = [
          filters.boundingBox.latitudeTopLeft,
          filters.boundingBox.longitudeTopLeft,
          filters.boundingBox.latitudeBottomRight,
          filters.boundingBox.longitudeBottomRight
        ].join(',');
      }
      return boundingBox;
    }
    return null;
  },
  filtersAreActive(filters) {
    return (
      !!filters.minRating ||
      !!filters.queryTerm ||
      this.hasFiltersSet(filters.completed) ||
      this.hasFiltersSet(filters.linked) ||
      this.hasFiltersSet(filters.difficulty) ||
      this.hasFiltersSet(filters.route_type) ||
      this.hasFiltersSet(filters.visitor_usage) ||
      this.hasFiltersSet(filters.activities) ||
      this.hasFiltersSet(filters.features) ||
      this.hasFiltersSet(filters.access) ||
      this.hasFiltersSet(filters.areas) ||
      this.hasFiltersSet(filters.cities) ||
      this.hasFiltersSet(filters.states) ||
      this.hasFiltersSet(filters.countries) ||
      this.hasAlteredSliderFilter(filters.lengths) ||
      this.hasAlteredSliderFilter(filters.elevation_gain) ||
      this.hasAlteredSliderFilter(filters.time_duration)
    );
  },
  hasFiltersSet(filters) {
    const selectedFilters = Object.keys(filters).filter(key => {
      return filters[key].selected;
    });
    return selectedFilters.length > 0;
  },
  hasAlteredSliderFilter(filter) {
    return filter.value[0] !== 0 || filter.value[1] !== -1;
  },
  addUrlParamFromSingleFilter(urlParams, filter, paramKey) {
    if (!filter) {
      return;
    }
    urlParams.push(`${paramKey}=${filter}`);
  },
  addUrlParamFromMultiFilter(urlParams, filter, paramKey) {
    if (!filter) {
      return;
    }
    Object.keys(filter).forEach(key => {
      if (filter[key].selected) {
        urlParams.push(`${paramKey}[]=${key}`);
      }
    });
  },
  addUrlParamFromSliderFilter(urlParams, filter, paramKey) {
    if (!filter || isEqual(filter.value, [0, -1])) {
      return;
    }
    urlParams.push(`${paramKey}[]=${filter.value[0]}`);
    urlParams.push(`${paramKey}[]=${filter.value[1]}`);
  },
  createUrlFromPageAndFilters(page, filters, selectedObject) {
    const urlParams = [];

    // If not explore or no location object or location object is not a city/park (slug split < 3 ie no country,state,city)
    if (filters.boundingBox && page !== PageStrings.EXPLORE_LIFELINE_PAGE) {
      urlParams.push([
        `b_tl_lat=${filters.boundingBox.latitudeTopLeft}`,
        `b_tl_lng=${filters.boundingBox.longitudeTopLeft}`,
        `b_br_lat=${filters.boundingBox.latitudeBottomRight}`,
        `b_br_lng=${filters.boundingBox.longitudeBottomRight}`
      ]);
    }

    this.addUrlParamFromSingleFilter(urlParams, page === PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE, 'cc');
    this.addUrlParamFromSingleFilter(urlParams, filters.queryTerm, 'q');
    this.addUrlParamFromSingleFilter(urlParams, filters.mobileMap, 'mobileMap');
    this.addUrlParamFromSingleFilter(urlParams, filters.minRating, 'rating_min');

    this.addUrlParamFromMultiFilter(urlParams, filters.completed, 'c');
    this.addUrlParamFromMultiFilter(urlParams, filters.activities, 'a');
    this.addUrlParamFromMultiFilter(urlParams, filters.features, 'f');
    this.addUrlParamFromMultiFilter(urlParams, filters.access, 'f');
    this.addUrlParamFromMultiFilter(urlParams, filters.areas, 'ar');
    this.addUrlParamFromMultiFilter(urlParams, filters.difficulty, 'diff');
    this.addUrlParamFromMultiFilter(urlParams, filters.route_type, 'route');
    this.addUrlParamFromMultiFilter(urlParams, filters.visitor_usage, 'usage');

    this.addUrlParamFromSliderFilter(urlParams, filters.elevation_gain, 'elev');
    this.addUrlParamFromSliderFilter(urlParams, filters.lengths, 'length');

    let extraFilters = urlParams.flat().join('&');
    let path = '/explore';
    if (
      page === PageStrings.EXPLORE_ALL_PAGE &&
      filters.locationObject &&
      filters.locationObject.slug &&
      filters.locationObject.slug.split('/').length > 2
    ) {
      path += `/${filters.locationObject.slug}`;
    } else if (page === PageStrings.EXPLORE_ALL_PAGE) {
      path = '/explore';
    } else if (
      page === PageStrings.EXPLORE_FAVORITE_PAGE ||
      page === PageStrings.EXPLORE_COMPLETED_PAGE ||
      page === PageStrings.EXPLORE_CUSTOM_PAGE ||
      page === PageStrings.EXPLORE_LIFELINE_PAGE
    ) {
      path = typeof window !== 'undefined' ? window.location.pathname : '/explore';
    } else if (page === PageStrings.EXPLORE_USERS_TRACKS_PAGE) {
      if (filters.initialUserSlug) path = `/members/${filters.initialUserSlug}/recordings`;
    } else if (page === PageStrings.EXPLORE_TRAIL_MAP_PAGE) {
      path = `/explore/${selectedObject.slug}`;
      const u = getQueryStringValue('u');
      if (u === 'm' || u === 'i') {
        path += `?u=${u}`;
      }
      extraFilters = [];
    } else if (page === PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE) {
      path = typeof window !== 'undefined' ? window.location.pathname : '/explore';
      const u = getQueryStringValue('u');
      if (u === 'm' || u === 'i') {
        path += `?u=${u}`;
      }
      extraFilters = [];
    } else if (page === PageStrings.EXPLORE_USERS_MAPS_PAGE) {
      if (filters.initialUserSlug) path = `/members/${filters.initialUserSlug}/maps`;
    } else if (page === PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE) {
      path = `/explore/map/${selectedObject.slug}`;
      extraFilters = [];
    }
    if (extraFilters.length > 0 && typeof window !== 'undefined') {
      return window.checkLinkAndAppendLanguage(`${path}?${extraFilters}`);
    }
    if (typeof window !== 'undefined') {
      return window.checkLinkAndAppendLanguage(path);
    }
    return path;
  },
  customSearch(items, filters, context, isMobileWidth) {
    let results = JSON.parse(JSON.stringify(items)) || [];

    if (filters.boundingBox != null && isMobileWidth) {
      // if a place is selected, adjust bounding box for mobile size, otherwise show all tracks by blowing away bounds
      if (filters.locationObject) {
        const degreeOffset = 0.703;
        filters.boundingBox.latitudeTopLeft += degreeOffset;
        filters.boundingBox.longitudeTopLeft -= degreeOffset;
        filters.boundingBox.latitudeBottomRight -= degreeOffset;
        filters.boundingBox.longitudeBottomRight += degreeOffset;
      } else {
        filters.boundingBox = null;
      }
    }

    if (filters.boundingBox && results) {
      const checkInBoundingBox = (lat, lng) => {
        // Super zoomed out, just show em all
        if (filters.zoomLevel != null && filters.zoomLevel < 4) return true;
        if (filters.boundingBox.longitudeTopLeft > filters.boundingBox.longitudeBottomRight) {
          if (
            lat < parseFloat(filters.boundingBox.latitudeTopLeft) &&
            lat > parseFloat(filters.boundingBox.latitudeBottomRight) &&
            ((lng > parseFloat(filters.boundingBox.longitudeTopLeft) && lng < 180) ||
              (lng < parseFloat(filters.boundingBox.longitudeBottomRight) && lng > -180))
          )
            return true;
        } else if (
          lat < parseFloat(filters.boundingBox.latitudeTopLeft) &&
          lat > parseFloat(filters.boundingBox.latitudeBottomRight) &&
          lng < parseFloat(filters.boundingBox.longitudeBottomRight) &&
          lng > parseFloat(filters.boundingBox.longitudeTopLeft)
        ) {
          return true;
        }
        return false;
      };
      results = results.filter(item => {
        const lat = parseFloat(item._geoloc.lat);
        const lng = parseFloat(item._geoloc.lng);
        return checkInBoundingBox(lat, lng);
      });
    }
    if (filters.minRating && results) {
      results = results.filter(item => {
        return item.rating >= filters.minRating;
      });
    }
    const selectedActivityFilterValues = Object.keys(filters.activities)
      .map(activityFilterKey => {
        const activityFilter = filters.activities[activityFilterKey];
        if (activityFilter && activityFilter.selected) {
          return activityFilter.value;
        }
        return null;
      })
      .filter(item => !!item);

    if (selectedActivityFilterValues.length > 0 && results) {
      results = results.filter(item => {
        return item.activity && selectedActivityFilterValues.indexOf(item.activity.uid) >= 0;
      });
    }

    let minLength = null;
    let maxLength = null;
    if (filters.lengths) {
      if (filters.lengths.value[1] !== -1) {
        maxLength = filters.lengths.value[1];
      }
      if (filters.lengths.value[0] > 0) {
        minLength = filters.lengths.value[0];
      }
    }

    let minElevationGain = null;
    let maxElevationGain = null;
    if (filters.elevation_gain) {
      if (filters.elevation_gain.value[1] !== -1) {
        maxElevationGain = filters.elevation_gain.value[1];
      }
      if (filters.elevation_gain.value[0] > 0) {
        minElevationGain = filters.elevation_gain.value[0];
      }
    }

    if (filters.linked) {
      // set value of 'selected'
      let selected = null;
      if (filters.linked.unlinked.selected) {
        selected = 'unlinked';
      }
      if (filters.linked.linked.selected) {
        if (selected === 'unlinked') {
          selected = null;
        } else {
          selected = 'linked';
        }
      }
      // use value of 'selected' to determine how to filter items by trail_id
      if (selected && results) {
        results = results.filter(item => {
          if (selected === 'unlinked') {
            return !item.trail_id && !item.private; // show non-linked, public results
          }
          if (selected === 'linked') {
            return !!item.trail_id;
          }
          return false;
        });
      }
    }

    results = results.filter(item => {
      if (maxLength == null && minLength == null) return true;
      if (maxLength == null && item.distance > minLength) return true;
      if (minLength == null && item.distance < maxLength) return true;
      if (maxLength < 0 && item.distance > minLength) return true;
      if (item.distance < maxLength && item.distance > minLength) return true;
      return false;
    });

    results = results.filter(item => {
      if (maxElevationGain == null && minElevationGain == null) return true;
      if (maxElevationGain == null && item.elevation_gain > minElevationGain) return true;
      if (minElevationGain == null && item.elevation_gain < maxElevationGain) return true;
      if (maxElevationGain < 0 && item.elevation_gain > minElevationGain) return true;
      if (item.elevation_gain < maxElevationGain && item.elevation_gain > minElevationGain) return true;
      return false;
    });

    if (filters.queryTerm && filters.queryTerm.length > 0) {
      results = results.filter(item => {
        const name = item.name ? item.name.toLowerCase() : '';
        const dateStr = new Date(item.created_at)
          .toLocaleDateString(context.languageRegionCode, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
          .toLowerCase();
        const query = filters.queryTerm.toLowerCase();
        return `${name} ${dateStr}`.search(query) >= 0;
      });
    }
    const selectedFilterKey = Object.keys(filters.sort).filter(itemKey => {
      return filters.sort[itemKey].selected;
    })[0];

    if (selectedFilterKey) {
      if (selectedFilterKey === 'date') {
        results = results.sort((itemA, itemB) => {
          const dateA = new Date(itemA.created_at);
          const dateB = new Date(itemB.created_at);
          if (filters.sort[selectedFilterKey].direction === -1) return dateA - dateB;
          return dateB - dateA;
        });
      } else if (selectedFilterKey === 'name') {
        results = results.sort((itemA, itemB) => {
          const nameA = itemA.name.toUpperCase();
          const nameB = itemB.name.toUpperCase();
          if (filters.sort[selectedFilterKey].direction === 1) {
            if (nameA > nameB) return 1;
            if (nameA < nameB) return -1;
            return 0;
          }

          if (nameA < nameB) return 1;
          if (nameA > nameB) return -1;
          return 0;
        });
      }
    }
    return results;
  }
};

export { SearchFiltersUtil };
